<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { AUTH_PROVIDERS } from './authProviders'
import { useLoginSection, type LoginSectionProps } from './useLoginSection'
import { httpPost } from '@/lib/http'
import { currentUser } from '@/lib/currentUser'
import { getIntlState } from '@/lib/intl'

import RecoveryModal from './modals/RecoveryModal.vue'
import StudentsModal from './modals/StudentsModal.vue'

const props = defineProps<LoginSectionProps>()

const emit = defineEmits<{
  (e: 'change', section: string): void
  (e: 'update:shared-data', data: Record<string, unknown>): void
}>()

const { changeSection, setDataItem, onAuthSuccess } = useLoginSection(props, emit)

const recoveryModal = ref<{ $open: (email: string) => void } | null>(null)
const showPassword = ref(false)
const error = ref('')
const credentials = reactive({ username: '', password: '' })
const showRecoveryModal = ref(false)
const showStudentsModal = ref(false)
const submitting = ref(false)
const students = ref<Array<{ id: number; first_name: string; last_name: string; login_token: string }>>([])
const googleBlockedModal = ref(false)

const authUrls = computed(() => {
  const isIntl = getIntlState()
  return AUTH_PROVIDERS.filter(item => !(item.ruOnly && isIntl)).map(item => {
    const url = new URL(`/users/auth_${item.slug}`, window.location.href)
    url.search = window.location.search
    return { key: item.slug, url: url.toString() }
  })
})

onMounted(() => {
  if (window.location.hash && window.location.hash.includes('@')) {
    credentials.username = window.location.hash.slice(1)
    window.location.hash = ''
  }
})

function openRecoveryModal() {
  recoveryModal.value?.$open(credentials.username)
}

async function submit() {
  if (submitting.value) return
  error.value = ''
  if (!credentials.username.trim() || !credentials.password.trim()) {
    await nextTick()
    error.value = 'Заполните все поля.'
    return
  }

  submitting.value = true
  const result = await httpPost<{
    success: boolean
    message: string
    user: { id: number; first_name: string; last_name: string }
  }>('/users/request_login', credentials)
  submitting.value = false

  if (!result.success) {
    error.value = result.message
    return
  }

  const { user } = result
  currentUser.id = user.id
  currentUser.firstName = user.first_name
  currentUser.lastName = user.last_name
  setDataItem('role', undefined)
  onAuthSuccess()
}

async function loginAsStudent() {
  if (submitting.value) return
  if (!credentials.username.trim() || !credentials.password.trim()) {
    error.value = 'Заполните все поля.'
    return
  }

  submitting.value = true
  const result = await httpPost<{
    success: boolean
    message: string
    data: Array<{ id: number; first_name: string; last_name: string; login_token: string }>
  }>('/users/request_students_for_login', credentials)
  submitting.value = false

  if (!result.success) {
    error.value = result.message
    return
  }

  students.value = result.data
  showStudentsModal.value = true
}
</script>

<template>
  <div class="signin-form">
    <h1 class="text-h3 text-center">Войдите в свой аккаунт</h1>

    <RecoveryModal ref="recoveryModal" v-model="showRecoveryModal" />
    <StudentsModal v-model="showStudentsModal" :students="students" />

    <!-- TODO: get seminar from context (WIP Backend) -->
    <h2 v-if="false" class="text-title-bold text-center my-1 blue-text">Семинар Clumsy and Gracie</h2>

    <p v-if="false" class="text-body text-center">
      Для доступа к мероприятию и материалам пройдите регистрацию или войдите на сайт
    </p>

    <ul class="signin-form__oauth mt-4">
      <li v-for="provider in authUrls" :key="provider.key">
        <a
          v-if="provider.key === 'google'"
          @click="
            () => {
              googleBlockedModal = true
            }
          "
        >
          <v-icon size="32">auth-providers/{{ provider.key }}</v-icon>
        </a>
        <a v-else :href="provider.url">
          <v-icon size="32">auth-providers/{{ provider.key }}</v-icon>
        </a>
      </li>
    </ul>

    <div class="signin-form__divider my-3">
      <hr />
      <span class="mx-1">Или</span>
      <hr />
    </div>

    <form autocomplete="on" @submit.prevent="submit">
      <!-- TODO: phone authentication -->
      <!-- <v-field label="Эл. почта или номер телефона"> -->
      <v-field label="Эл. почта" class="mt-3">
        <!-- <v-input placeholder="Введите вашу почту или телефон"></v-input> -->
        <v-input
          v-model.trim="credentials.username"
          placeholder="Введите вашу почту"
          type="text"
          name="username"
          autocomplete="username"
          :error="error ? ' ' : ''"
        />
      </v-field>

      <v-field class="mt-2">
        <template #label>
          <div class="d-flex justify-space-between align-center">
            Пароль
            <v-link class="text-caption" mode="primary" type="button" tabindex="-1" @click="openRecoveryModal">
              Забыли пароль?
            </v-link>
          </div>
        </template>
        <v-input
          v-model.trim="credentials.password"
          placeholder="Введите пароль"
          :type="showPassword ? 'text' : 'password'"
          name="username"
          autocomplete="current-password"
          :error="error"
        >
          <template #append>
            <v-icon
              class="signin-form__hide-btn"
              :class="showPassword && 'signin-form__hide-btn_active'"
              @click="showPassword = !showPassword"
            >
              mdi-eye
            </v-icon>
          </template>
        </v-input>

        <v-link
          v-if="error"
          class="mt-3 text-caption"
          mode="primary"
          type="button"
          @click="() => changeSection(LoginSection.RoleSelect)"
        >
          Создать аккаунт
        </v-link>
      </v-field>

      <v-button type="submit" mode="primary filled large block" class="mt-5" :disabled="submitting"> Войти </v-button>

      <v-button
        v-if="query.get('student_login') === 'true' && !query.has('purpose')"
        type="button"
        mode="primary flat large block"
        class="mt-2"
        :disabled="submitting"
        @click="loginAsStudent"
      >
        Войти как ребенок
      </v-button>
    </form>

    <v-rich-modal title="Недоступно" v-model="googleBlockedModal">
      <div class="signin-form__google-blocked-modal">
        <div class="d-flex align-center justify-center mb-3 mt-1">
          <v-icon size="64">auth-providers/google</v-icon>
          <v-icon size="32" class="blue-text mx-4">mdi-arrow-right</v-icon>
          <v-icon size="64">logo-small</v-icon>
        </div>

        <p class="text-title-bold">Вход через Google в РФ больше недоступен по закону.</p>
        <p class="mt-2">
          <b>Чтобы войти в свой аккаунт</b> воспользуйтесь функцией
          <v-link
            mode="primary"
            @click="
              () => {
                openRecoveryModal()
                googleBlockedModal = false
              }
            "
            >восстановления пароля</v-link
          >: введите ваш E-Mail от аккаунта Google, с которым регистрировались на сайте.
        </p>
        <p class="mt-2">
          Если у вас нет аккаунта, вы можете
          <v-link @click="() => changeSection(LoginSection.RoleSelect)" mode="primary">зарегистрироваться</v-link> на
          сайте, используя ваш E-Mail от Google.
        </p>
      </div>
    </v-rich-modal>
  </div>
</template>

<style lang="scss">
.signin-form {
  @include desktop {
    width: 320px !important;
    margin: 0 auto;
  }

  &__oauth {
    display: grid;
    grid-auto-flow: column;
    justify-content: center;
    grid-gap: 0 24px;

    & > li {
      width: 64px;
      height: 64px;
      background: $lightenGray;
      border-radius: 8px;

      & > a {
        display: block;
        width: 64px;
        height: 64px;
        padding: 16px;
      }
    }
  }

  &__google-blocked-modal {
    @include desktop {
      max-width: 450px;
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    color: $darkGray;

    & > hr {
      flex: 1;
      height: 1px;
      border: none;
      border-bottom: 1px solid $lightGray;
    }
  }

  &__hide-btn {
    cursor: pointer;
    color: $gray;
    transition: color 0.2s ease;

    &_active {
      color: $blue;
    }
  }
}
</style>
